/**
 * Customer Sales History Modal
 * Shows all sales for a specific customer
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { Modal } from '../../components/ui/modal';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Card, CardContent } from '../../components/ui/card';
import { 
  ShoppingBag, 
  Calendar, 
  Receipt, 
  TrendingUp, 
  Package,
  CreditCard,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export function CustomerSalesModal({ open, onOpenChange, customer }) {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSale, setExpandedSale] = useState(null);

  useEffect(() => {
    if (open && customer?.id) {
      loadSales();
    }
  }, [open, customer?.id]);

  const loadSales = async () => {
    if (!customer?.id) return;
    
    setLoading(true);
    try {
      // Load sales for this customer
      const res = await api.get(`/sales?customer_id=${customer.id}&limit=50`);
      const salesData = res.data?.sales || res.data || [];
      setSales(salesData);
      
      // Calculate stats
      const totalSpent = salesData.reduce((sum, s) => sum + (s.total || 0), 0);
      const avgTicket = salesData.length > 0 ? totalSpent / salesData.length : 0;
      const lastPurchase = salesData.length > 0 ? salesData[0].created_at : null;
      
      setStats({
        totalPurchases: salesData.length,
        totalSpent,
        avgTicket,
        lastPurchase,
      });
    } catch (err) {
      console.error('Error loading sales:', err);
      toast.error('Erro ao carregar histórico de vendas');
    } finally {
      setLoading(false);
    }
  };

  const toggleSaleDetails = (saleId) => {
    setExpandedSale(expandedSale === saleId ? null : saleId);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Histórico de Compras - ${customer?.name || 'Cliente'}`}
      description="Visualize todas as compras realizadas por este cliente"
      size="lg"
      showConfirm={false}
      cancelText="Fechar"
      onCancel={() => onOpenChange(false)}
      footer={
        <>
          <Button variant="outline" onClick={loadSales} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <ShoppingBag className="h-4 w-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Compras</span>
                  </div>
                  <p className="text-xl font-bold mt-1">{stats.totalPurchases}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-500" />
                    <span className="text-xs text-muted-foreground">Total Gasto</span>
                  </div>
                  <p className="text-xl font-bold mt-1 font-mono">{formatCurrency(stats.totalSpent)}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Ticket Médio</span>
                  </div>
                  <p className="text-xl font-bold mt-1 font-mono">{formatCurrency(stats.avgTicket)}</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-orange-500" />
                    <span className="text-xs text-muted-foreground">Última Compra</span>
                  </div>
                  <p className="text-sm font-medium mt-1">
                    {stats.lastPurchase ? formatDate(stats.lastPurchase) : 'Nunca'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sales List */}
          <div className="border rounded-lg">
            <div className="p-3 bg-muted/50 border-b">
              <h3 className="font-medium flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Histórico de Compras ({sales.length})
              </h3>
            </div>
            
            <ScrollArea className="h-[350px]">
              {sales.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhuma compra registrada</p>
                </div>
              ) : (
                <div className="divide-y">
                  {sales.map((sale) => (
                    <div key={sale.id} className="p-3 hover:bg-muted/30 transition-colors">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleSaleDetails(sale.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Receipt className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">
                              Venda #{sale.sale_number || sale.id?.slice(-6)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {formatDateTime(sale.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-mono font-semibold text-primary">
                              {formatCurrency(sale.total)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {sale.items?.length || 0} itens
                            </p>
                          </div>
                          {expandedSale === sale.id ? (
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded Details */}
                      {expandedSale === sale.id && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          {/* Items */}
                          <div className="space-y-1">
                            {sale.items?.map((item, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-1 px-2 bg-muted/50 rounded">
                                <div className="flex items-center gap-2">
                                  <Package className="h-3 w-3 text-muted-foreground" />
                                  <span>{item.quantity}x {item.name || item.product_name}</span>
                                </div>
                                <span className="font-mono">
                                  {formatCurrency((item.unit_price || 0) * (item.quantity || 1))}
                                </span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Payment Info */}
                          {sale.payments?.length > 0 && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <CreditCard className="h-3 w-3" />
                              <span>
                                {sale.payments.map(p => p.payment_method_name || 'Pagamento').join(', ')}
                              </span>
                            </div>
                          )}
                          
                          {/* Status */}
                          <div className="flex items-center gap-2">
                            <Badge variant={sale.status === 'completed' ? 'default' : 'secondary'}>
                              {sale.status === 'completed' ? 'Finalizada' : sale.status}
                            </Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default CustomerSalesModal;
